import type { PropertyOption, PropertySortOption } from "./GlobalPropertyMappingSelector.types";

/**
 * Compute which properties are not assigned to any field.
 */
export function getUnassignedProperties(
	availableProperties: PropertyOption[],
	selectedValues: Partial<Record<string, PropertyOption[]>>,
	sortOption: PropertySortOption,
): PropertyOption[] {
	const assignedNames = new Set<string>();
	for (const props of Object.values(selectedValues)) {
		if (props) {
			for (const prop of props) {
				assignedNames.add(prop.name);
			}
		}
	}

	const unassigned = availableProperties.filter((p) => !assignedNames.has(p.name));

	return sortProperties(unassigned, sortOption);
}

/**
 * Sort properties by the given sort option.
 */
export function sortProperties(properties: PropertyOption[], sortOption: PropertySortOption): PropertyOption[] {
	const sorted = [...properties];
	switch (sortOption) {
		case "name-asc":
			return sorted.sort((a, b) => a.name.localeCompare(b.name));
		case "name-desc":
			return sorted.sort((a, b) => b.name.localeCompare(a.name));
	}
}

/**
 * Filter properties by a case-insensitive text query against name and examples.
 */
export function filterProperties(properties: PropertyOption[], filterText: string): PropertyOption[] {
	const query = filterText.trim().toLowerCase();
	if (!query) return properties;
	return properties.filter(
		(p) => p.name.toLowerCase().includes(query) || p.examples?.some((ex) => ex.toLowerCase().includes(query)),
	);
}

/**
 * Handle multi-select click logic for property cards.
 * Returns the new selection set and the last-clicked property name.
 */
export function handlePropertySelection(
	currentSelection: Set<string>,
	clickedName: string,
	shiftKey: boolean,
	lastClickedName: string | null,
	orderedList: PropertyOption[],
): { selection: Set<string>; lastClicked: string } {
	if (shiftKey && lastClickedName) {
		const anchorIdx = orderedList.findIndex((p) => p.name === lastClickedName);
		const targetIdx = orderedList.findIndex((p) => p.name === clickedName);
		if (anchorIdx !== -1 && targetIdx !== -1) {
			const start = Math.min(anchorIdx, targetIdx);
			const end = Math.max(anchorIdx, targetIdx);
			const next = new Set(currentSelection);
			for (let i = start; i <= end; i++) {
				next.add(orderedList[i].name);
			}
			return { selection: next, lastClicked: clickedName };
		}
	}

	// Normal toggle
	const next = new Set(currentSelection);
	if (next.has(clickedName)) {
		next.delete(clickedName);
	} else {
		next.add(clickedName);
	}
	return { selection: next, lastClicked: clickedName };
}
